import type { DashboardMenuItemContent } from "./DashboardMenuItem";

import * as React from "react";

import { DashboardSidebarSeparator } from "@editor/components/shops-ui/DashboardSidebarSeperator";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { routes } from "@editor/utils/router";

import { useEditorStoreActions } from "@/features/react-editor/store/editorStore";
import {
  SidebarGroup,
  SidebarMenuItem,
  useSidebar,
} from "@replo/design-system/components/shadcn/core/sidebar";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  ChartLine,
  FlaskConical,
  GalleryVerticalEnd,
  Home,
  MessageSquareMore,
  PackageOpen,
  PaintBucket,
  Settings,
  SwatchBook,
} from "lucide-react";
import { generatePath } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";
import z from "zod";

import DashboardMenuItem from "./DashboardMenuItem";

export const DashboardMenuItems = () => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId() ?? null;
  const { project } = useCurrentProjectContext();
  const { setPanelView } = useEditorStoreActions();

  const { open, openMobile, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isSidebarExpanded = isMobile ? openMobile : open;

  const [showStyleExtraction] = useLocalStorageState(
    "debug.showStyleExtraction",
    true,
    {
      schema: z.boolean(),
    },
  );

  const handleMenuItemClick = () => {
    if (openMobile) {
      toggleSidebar();
    }
  };

  const menuItems: (DashboardMenuItemContent | React.ReactElement)[] = [];
  if (project) {
    const shouldShowReploV2MenuItems = project.hostingType === "replo";
    const experimentListPath =
      workspaceId &&
      generatePath(routes.shops.experiments.list, {
        projectId: project.id,
      });
    if (shouldShowReploV2MenuItems) {
      menuItems.push(
        {
          title: "Home",
          path: `/shops/${project.id}/home`,
          href: `/shops/${project.id}/home`,
          onClick: () => {
            logEvent("dashboard.nav.click", {
              tab: "home",
              userId: user?.id ?? null,
              workspaceId,
            });
          },
          startEnhancer: <Home size={16} />,
        },
        {
          title: "Chat",
          path: `/shops/${project.id}/chat`,
          href: `/shops/${project.id}/chat`,
          onClick: () => {
            // NOTE (Kevin 2025-08-19): This is a workaround to ensure the main sidebar collapses when navigating to chat.
            // and also open panels like Version History don't reopen when going back to Chat.
            // Actual fix would be to create a shared context for chat state that both components can access,
            setPanelView("chat");
            logEvent("dashboard.nav.click", {
              tab: "chat",
              userId: user?.id ?? null,
              workspaceId,
            });
          },
          startEnhancer: <MessageSquareMore size={16} />,
        },
        <DashboardSidebarSeparator
          className={twMerge(!isSidebarExpanded ? "ml-0 mr-1" : "mx-0")}
        />,
      );
    }

    if (!shouldShowReploV2MenuItems) {
      menuItems.push({
        title: "Pages",
        path: `/shops/${project.id}/site`,
        additionalPath: "/home",
        href: `/shops/${project.id}/site`,
        startEnhancer: <GalleryVerticalEnd size={16} />,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "shop_site",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
      });
    }
    menuItems.push({
      title: "Analytics",
      path: `/shops/${project.id}/analytics`,
      href: isFeatureEnabled("analytics-v2-data-model")
        ? `/shops/${project.id}/analytics/home`
        : `/shops/${project.id}/analytics/insights`,
      onClick: () => {
        logEvent("dashboard.nav.click", {
          tab: isFeatureEnabled("analytics-v2-data-model")
            ? "analytics_overview"
            : "analytics_insights",
          userId: user?.id ?? null,
          workspaceId,
        });
      },
      startEnhancer: <ChartLine size={16} />,
    });

    if (experimentListPath) {
      menuItems.push({
        title: "A/B Testing",
        path: experimentListPath,
        href: experimentListPath,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "experiments",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
        startEnhancer: <FlaskConical size={16} />,
      });
    }

    if (shouldShowReploV2MenuItems) {
      menuItems.push(
        <DashboardSidebarSeparator
          className={twMerge(!isSidebarExpanded ? "ml-0 mr-1" : "mx-0")}
        />,
      );
    }

    if (shouldShowReploV2MenuItems) {
      if (showStyleExtraction) {
        menuItems.push({
          title: "Style Extraction",
          path: `/shops/${project.id}/style-extraction`,
          href: `/shops/${project.id}/style-extraction`,
          onClick: () => {
            logEvent("dashboard.nav.click", {
              tab: "style_extraction",
              userId: user?.id ?? null,
              workspaceId,
            });
          },
          startEnhancer: <PaintBucket size={16} />,
        });
      }

      menuItems.push({
        title: "Design Library",
        path: `/shops/${project.id}/design-library`,
        href: `/shops/${project.id}/design-library`,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "design_library",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
        startEnhancer: <SwatchBook size={16} />,
      });
    }

    if (
      shouldShowReploV2MenuItems &&
      isFeatureEnabled("products-shops-menu-item")
    ) {
      menuItems.push({
        title: "Products",
        path: `/shops/${project.id}/products`,
        href: `/shops/${project.id}/products`,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "products",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
        startEnhancer: <PackageOpen size={16} />,
      });
    }

    menuItems.push({
      title: "Settings",
      path: `/shops/${project.id}/settings`,
      href: `/shops/${project.id}/settings`,
      onClick: () => {
        logEvent("dashboard.nav.click", {
          tab: "settings",
          userId: user?.id ?? null,
          workspaceId,
        });
      },
      startEnhancer: <Settings size={16} />,
    });
  } else if (workspaceId) {
    menuItems.push({
      title: "Settings",
      path: `/workspace/${workspaceId}/settings`,
      href: `/workspace/${workspaceId}/settings`,
      onClick: () => {
        logEvent("dashboard.nav.click", {
          tab: "settings",
          userId: user?.id ?? null,
          workspaceId,
        });
      },
      startEnhancer: <Settings size={16} />,
    });
  }

  return (
    <SidebarGroup
      className={twMerge(
        "flex w-full flex-col gap-3 p-3 ",
        !isSidebarExpanded && "ml-1",
      )}
    >
      {filterNulls(menuItems).map((item, index) => {
        if (React.isValidElement(item)) {
          return React.cloneElement(item, { key: index });
        }

        return (
          <SidebarMenuItem key={index} onClick={handleMenuItemClick}>
            <DashboardMenuItem item={item as DashboardMenuItemContent} />
          </SidebarMenuItem>
        );
      })}
    </SidebarGroup>
  );
};
